import { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { formatMoney } from '@/lib/format';
import { LayoutDashboard, Archive, BarChart3, Package, Users, UserCircle, Gem, LogOut, MessageSquare, Palette, PanelLeftClose, PanelLeftOpen, Menu, X, Trash2, ChevronDown, Bell, DollarSign, Settings, Bug, HelpCircle, Trophy, BookOpen, Sun, Moon, Zap, Check, RefreshCw, Sparkles, Library, Gift, BrainCircuit} from 'lucide-react';
import { showReferButton, REFER_LABEL, REFER_TARGET } from '@/lib/referral';
import { useEasterEgg, SidebarBadges, TierCelebration } from './JoyFeatures';
import { TierGem } from './GemGraphics';
import { SITE_LOOK_ROLES } from '@/lib/useSiteLook';
import PixallyAd from './PixallyAd';

const UnreadRefreshContext = createContext(() => {});
export const useUnreadRefresh = () => useContext(UnreadRefreshContext);
import { getSeasonalTheme, getSeasonalStyles } from '@/lib/seasonal';
import ProductTour, { startTour } from './ProductTour';
import { Wordmark } from './Wordmark';
import { CLIENT_STEPS, EDITOR_STEPS, ADMIN_STEPS, STAFF_STEPS } from '@/lib/tour-steps';
import { OPEN_BUG_REPORT } from '@/lib/bugReport';
import { FootagePullNewVersionBanner } from './FootagePullUpdate';
import { BANNER_ROLES } from '@/lib/footagePull';
import { isTrainingOwner } from '@/lib/trainingOwner';
import { requestOpenProject } from '@/lib/openProject';


// Always-visible gem-bag tier widget for the client sidebar (top, under the
// logo). Reads live from GET /loyalty/my-tier (backend-authoritative) — never
// the duplicated lib/api.js tier constant. Spend-based copy only ("$X to Tier").
function GemTierWidget({ data, collapsed, isMobile, onOpen }) {
  if (data === undefined) {
    // loading: thin skeleton when expanded; nothing when collapsed (avoid flicker)
    if (collapsed && !isMobile) return null;
    return <div className="mx-3 mt-3 mb-1 h-9 rounded-xl bg-white/[0.03] animate-pulse" data-testid="gem-tier-widget-loading" />;
  }
  if (data === null) return null; // graceful hide (error / non-client)

  const cur = data.current_tier || {};
  const isCustom = !!data.custom_discount_enabled;
  const next = isCustom ? null : data.next_tier;
  const color = cur.badge_color || data.gem_tier?.badge_color || '#94A3B8';
  const discount = cur.discount || 0;
  const tierName = cur.name || 'Standard';
  const effective = data.effective_spend || 0;
  const spendToNext = data.spend_to_next || 0;

  let fillPct = 0;
  if (next) {
    const floor = cur.min_spend ?? 0;
    const span = (next.min_spend ?? 0) - floor;
    fillPct = span > 0 ? Math.min(100, Math.max(0, ((effective - floor) / span) * 100)) : 0;
  }

  const tooltip = next
    ? `${tierName} · ${discount}% off · $${formatMoney(spendToNext)} to ${next.name}`
    : `${tierName} · ${discount}% off${isCustom ? ' (custom)' : ' · Top tier'}`;

  // Collapsed sidebar: colored gem badge only, details on hover
  if (collapsed && !isMobile) {
    return (
      <div className="flex justify-center my-3" data-testid="gem-tier-widget-collapsed">
        {/* Square, as the whole client side is (site-look.css). Not rounded-full in the markup either: the light
            theme repaints anything round that carries an inline background (index.css, "Avatars") and took the
            tier's colour away. */}
        <button onClick={onOpen} title={tooltip} aria-label={tooltip}
          className="w-9 h-9 flex items-center justify-center transition-transform hover:scale-105"
          style={{ background: `${color}1A`, border: `1.5px solid ${color}55` }}>
          <TierGem color={color} size={20} />
        </button>
      </div>
    );
  }

  // Mobile drawer: condensed single row (badge + tier + "$X to next" + discount)
  // — keeps the key info without the tall progress card eating drawer space.
  if (isMobile) {
    return (
      <button onClick={onOpen} data-testid="gem-tier-widget" title={tooltip}
        className="mx-3 mt-2 mb-1 w-[calc(100%-1.5rem)] flex items-center gap-2.5 text-left px-3 py-2 rounded-lg border bg-white/[0.03] active:bg-white/[0.06] transition-colors"
        style={{ borderColor: `${color}33` }}>
        <span className="w-6 h-6 flex items-center justify-center shrink-0">
          <TierGem color={color} size={16} />
        </span>
        <span className="font-sans text-xs font-semibold shrink-0" style={{ color }} data-testid="gem-tier-name">{tierName}</span>
        <span className="font-sans text-[10px] text-zinc-400 truncate" data-testid="gem-tier-progress-text">
          {next ? `· $${formatMoney(spendToNext)} to ${next.name}` : (isCustom ? '· Custom rate' : '· Top tier')}
        </span>
        {next ? (
          <span className="ml-auto font-sans text-[10px] font-bold text-ug-gold shrink-0" data-testid="gem-tier-next-discount">{next.discount}% off</span>
        ) : (discount > 0 && <span className="ml-auto font-sans text-[10px] font-bold text-ug-gold shrink-0" data-testid="gem-tier-discount">{discount}% off</span>)}
      </button>
    );
  }

  return (
    <button onClick={onOpen} data-testid="gem-tier-widget" title={tooltip}
      className="tap-auto mx-3 mt-2 mb-1 w-[calc(100%-1.5rem)] text-left px-2.5 py-1.5 rounded-lg border bg-gradient-to-br from-white/[0.04] to-transparent hover:from-white/[0.07] transition-all duration-300"
      style={{ borderColor: `${color}33` }}>
      {/* Row 1: the tier's stone + full tier name + current discount — never truncates */}
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="w-4 h-4 flex items-center justify-center shrink-0">
          <TierGem color={color} size={14} />
        </span>
        <span className="tap-auto font-sans text-[11px] font-semibold whitespace-nowrap" style={{ color }} data-testid="gem-tier-name">{tierName}</span>
        <span className="tap-auto font-sans text-[10px] text-zinc-400 whitespace-nowrap" data-testid="gem-tier-current-discount">({discount}% off)</span>
      </div>
      {/* Row 2: progress text on its own row, directly above the meter */}
      {next ? (
        <>
          <p className="tap-auto font-sans text-[9px] text-zinc-400 truncate mt-1" data-testid="gem-tier-progress-text">
            ${formatMoney(spendToNext)} to {next.name} · <span className="text-ug-gold font-bold" data-testid="gem-tier-next-discount">{next.discount}% off</span>
          </p>
          {/* Row 3: the meter. Square, and not rounded-full: the light theme's avatar rule repainted its fill (the
              tier's colour) a warm grey. */}
          <div className="h-1 bg-white/[0.06] overflow-hidden mt-1">
            <div className="h-full transition-all" style={{ width: `${fillPct}%`, backgroundColor: color }} />
          </div>
        </>
      ) : (
        <p className="tap-auto font-sans text-[9px] text-zinc-500 truncate mt-1" data-testid="gem-tier-progress-text">
          {isCustom ? 'Custom rate applied' : 'Top tier, max rewards'}
        </p>
      )}
    </button>
  );
}

// Nav items now use `children` for sub-items (Archived, Trash under Projects)
const NAV_ITEMS = {
  client: [
    // Archived here is the CLIENT'S own archive (app/archive.py): what they tidy
    // away leaves only their list, and what we archive never leaves it.
    { label: 'Active Projects', path: '/dashboard', icon: LayoutDashboard, children: [
      { label: 'Archived', path: '/archived', icon: Archive },
      { label: 'Trash', path: '/trash', icon: Trash2 },
    ]},
    { label: 'My Chats', path: '/client/chats', icon: MessageSquare },
    { label: 'Style Profile', path: '/style-profile', icon: Palette },
    { label: 'Pricing', path: '/pricing', icon: DollarSign },
    { label: 'Gem Bag', path: '/gem-bag', icon: Gem },
    { label: 'Help', path: '/help', icon: HelpCircle },
  ],
  editor: [
    { label: 'My Projects', path: '/dashboard', icon: LayoutDashboard, children: [
      { label: 'Archived', path: '/archived', icon: Archive },
      { label: 'Trash', path: '/trash', icon: Trash2 },
    ]},
    { label: 'My Chats', path: '/editor/chats', icon: MessageSquare },
    { label: 'Payments', path: '/editor/payments', icon: DollarSign },
    { label: 'My Stats', path: '/editor/stats', icon: Trophy },
    { label: 'Editor Guide', path: '/editor/guide', icon: BookOpen },
    { label: 'Redemption Cut', path: '/editor/redemptions', icon: Zap },
    { label: 'Settings', path: '/editor/settings', icon: Settings },
  ],
  admin: [
    { label: 'All Projects', path: '/dashboard', icon: LayoutDashboard, children: [
      { label: 'Archived', path: '/archived', icon: Archive },
      { label: 'Trash', path: '/trash', icon: Trash2 },
    ]},
    { label: 'All Chats', path: '/admin/chats', icon: MessageSquare },
    { label: 'Payments', path: '/admin/payments', icon: DollarSign },
    { label: 'Analytics', path: '/admin/analytics', icon: BarChart3 },
    { label: 'Products', path: '/admin/products', icon: Package },
    { label: 'Users', path: '/admin/users', icon: Users },
    { label: 'Redemption Cut', path: '/admin/redemptions', icon: Zap },
    { label: 'AI Training', path: '/admin/training', icon: BrainCircuit },
    { label: 'Settings', path: '/admin/settings', icon: Settings },
  ],
  staff: [
    { label: 'My Projects', path: '/dashboard', icon: LayoutDashboard, children: [
      { label: 'Archived', path: '/archived', icon: Archive },
      { label: 'Trash', path: '/trash', icon: Trash2 },
    ]},
    { label: 'All Chats', path: '/admin/chats', icon: MessageSquare },
    { label: 'Users', path: '/admin/users', icon: Users },
    { label: 'My Bonuses', path: '/admin/payments', icon: DollarSign },
  ],
  billing: [
    { label: 'All Projects', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Payments', path: '/admin/payments', icon: DollarSign },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { handleLogoClick, sparkle } = useEasterEgg();
  const seasonalTheme = getSeasonalTheme();
  const seasonalStyles = getSeasonalStyles(seasonalTheme);
  const location = useLocation();
  const navigate = useNavigate();
  // Reference Library is a per-editor capability, so the nav entry is added at
  // render time rather than living in NAV_ITEMS: an editor without the grant
  // gets a 403 from every endpoint behind it, and a link that only ever errors
  // is worse than no link.
  const trainingOwner = isTrainingOwner(user);
  const items = useMemo(() => {
    const all = NAV_ITEMS[user?.role] || NAV_ITEMS.client;
    // AI Training is the training owner's alone (lib/trainingOwner.js), never another admin's
    const base = trainingOwner ? all : all.filter(i => i.path !== '/admin/training');
    if (user?.role === 'editor' && user?.reference_access === 'all') {
      const next = [...base];
      const at = next.findIndex(i => i.path === '/editor/chats');
      next.splice(at < 0 ? next.length : at, 0,
        { label: 'Reference Library', path: '/editor/reference', icon: Library });
      return next;
    }
    return base;
  }, [user?.role, user?.reference_access, trainingOwner]);
  const [navExpanded, setNavExpanded] = useState({});
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('ug_sidebar_collapsed') === 'true'; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarMilestones, setSidebarMilestones] = useState([]);
  const [gemTier, setGemTier] = useState(undefined); // undefined=loading, null=hide(error/non-client), obj=data
  // Hard-refresh tip banner (Piece 2, 2026-06-10) — client + editor only.
  // SESSION-SCOPED via in-memory state (NOT localStorage): initialized true when
  // Layout mounts (i.e. on login, since Layout only renders inside protected
  // routes). Dismissal flips it false and PERSISTS across SPA navigation (Layout
  // doesn't remount on route change, so it never re-nags). It resets to true when
  // Layout remounts — on the next login (after logout) or a full page reload.
  const [showRefreshTip, setShowRefreshTip] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({ client_chats: 0, internal_chats: 0 });
  const [notifCount, setNotifCount] = useState(0);
  const prevChatTotal = useRef(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [bugModalOpen, setBugModalOpen] = useState(false);
  // Any page may open the dialog (lib/bugReport.js); the Help Center's "Report a Bug" does.
  useEffect(() => {
    const open = () => setBugModalOpen(true);
    window.addEventListener(OPEN_BUG_REPORT, open);
    return () => window.removeEventListener(OPEN_BUG_REPORT, open);
  }, []);
  // The `site-look` class moved OUT of here on 2026-09-28, to useSiteLook() in AppRoutes. It was never
  // Layout-scoped in effect -- it goes on <html> -- but setting it here meant the routes that render outside
  // Layout never got it, and /order-complete, the page a client sees straight after paying, was live in the old
  // look. Do not add it back: at app level it covers every route. lib/useSiteLook.js, KNOWN-ISSUES 0bd.
  const [bugText, setBugText] = useState('');
  const [notifs, setNotifs] = useState([]);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unpaidPaymentCount, setUnpaidPaymentCount] = useState(0);
  const [redemptionCount, setRedemptionCount] = useState(0);
  const [bugScreenshot, setBugScreenshot] = useState(null);
  const [bugSubmitted, setBugSubmitted] = useState(false);
  const notifRef = useRef(null);
  const userMenuRef = useRef(null);
  const userMenuRefMobile = useRef(null);
  const sidebarUserMenuRef = useRef(null);
  // Sign Out and Report Bug moved off the sidebar into this menu, so the nav
  // gets those two rows back; the profile row is now the only footer control.
  const [showSidebarMenu, setShowSidebarMenu] = useState(false);
  // The notification bell renders into BrandBar's #brandbar-actions slot. The
  // slot is a plain DOM node created by a sibling component, so it is resolved
  // after mount rather than assumed to exist during render.
  const [brandSlot, setBrandSlot] = useState(null);
  useEffect(() => { setBrandSlot(document.getElementById('brandbar-actions')); }, []);

  // Handle notification click — navigate to the relevant destination and mark as read
  const handleNotifClick = (n) => {
    // Mark this notification as read
    api.put('/notifications/read', { ids: [n.id] }).catch(() => {});
    setNotifs(prev => prev.filter(x => x.id !== n.id));
    setNotifCount(prev => Math.max(0, prev - 1));
    setShowNotifs(false);
    // Redemption notifications carry redemption_id, not project_id — route by
    // role to the matching redemptions surface (no thread-level deep link yet).
    if (n.type === 'redemption_chat' || n.type === 'redemption') {
      navigate(user?.role === 'editor' ? '/editor/redemptions' : '/admin/redemptions');
      return;
    }
    // Gem-bag tier celebration rows land on the Gem Bag page (clients only).
    if (n.type === 'tier_upgrade') {
      navigate('/gem-bag');
      return;
    }
    // Determine destination
    if (n.project_id) {
      let url = `/dashboard?open=${n.project_id}`;
      // Chat notifications: open the right chat tab
      if (n.system === 'client' && (n.type === 'chat' || n.type === 'mention' || n.type === 'urgent')) {
        url += '&chat=client';
      } else if (n.system === 'internal' && (n.type === 'chat' || n.type === 'mention' || n.type === 'urgent')) {
        // Only route to internal chat for non-client roles
        if (user?.role !== 'client') url += '&chat=internal';
      }
      // Review-related notifications: open review tab
      if (n.type === 'revision_request' || n.type === 'new_editor_draft') {
        url += '&tab=review';
      }
      // File requests live in the Files tab for BOTH the editor and admin panels.
      // Without this the notification landed on Review, which says nothing about the
      // thing it just told you about — the recipient has to already know where to
      // look, which is the dead end this feature exists to remove.
      if (n.type === 'file_request' || n.type === 'autoprep') {   // auto-prep's card is on the Files tab too
        url += '&tab=files';
      }
      navigate(url);
    } else if (n.type === 'no_order_7day') {
      navigate('/new-order');
    }
    // Notifications without a destination just get marked as read (panel closes)
  };

  useEffect(() => { try { localStorage.setItem('ug_sidebar_collapsed', String(collapsed)); } catch {} }, [collapsed]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Fetch milestones for sidebar badges
  useEffect(() => {
    if (user?.role === 'client' || user?.role === 'editor') {
      api.get('/milestones').then(r => setSidebarMilestones(r.data || [])).catch(() => {});
    }
  }, [user?.role]);

  // Gem-bag tier for the always-visible sidebar widget (client only, fetched once
  // at the layout level — persists across page navigation, not refetched per route).
  useEffect(() => {
    if (user?.role !== 'client') { setGemTier(null); return; }
    api.get('/loyalty/my-tier')
      .then(r => setGemTier(r.data))
      .catch(() => setGemTier(null)); // graceful: hide widget on failure
  }, [user?.role]);

  // Clear the one-shot tier celebration: server flag first (cross-device), then local.
  const clearTierCelebration = useCallback(() => {
    api.post('/loyalty/celebration-seen').catch(() => {});
    setGemTier(prev => prev ? { ...prev, tier_celebration_pending: null } : prev);
  }, []);

  // Close dropdowns on click outside
  useEffect(() => {
    const handler = (e) => {
      // `every ref non-null` would never close a menu once one of the surfaces
      // stopped rendering — check only the refs that are actually mounted.
      const inside = (ref) => ref.current && ref.current.contains(e.target);
      if (!inside(notifRef)) setShowNotifs(false);
      if (!inside(userMenuRef) && !inside(userMenuRefMobile)) setShowUserMenu(false);
      if (!inside(sidebarUserMenuRef)) setShowSidebarMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Pleasant notification chime using Web Audio API
  const playNotifSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
        gain.gain.setValueAtTime(0.08, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur);
      };
      playTone(830, 0, 0.15);    // High note
      playTone(1050, 0.12, 0.2); // Higher note (pleasant two-tone chime)
    } catch {}
  }, []);

  // Refresh unread counts — callable by children after mark-read
  const refreshUnreadCounts = useCallback(() => {
    api.get('/notifications/unread-counts').then(r => {
      prevChatTotal.current = (r.data.client_chats || 0) + (r.data.internal_chats || 0);
      setUnreadCounts(r.data);
    }).catch(() => {});
  }, []);

  // Poll for unread notification counts
  useEffect(() => {
    const fetchCounts = () => {
      refreshUnreadCounts();
      api.get('/notifications/count').then(r => setNotifCount(r.data.count)).catch(() => {});
    };
    // Set initial count without playing sound
    refreshUnreadCounts();
    api.get('/notifications/count').then(r => setNotifCount(r.data.count)).catch(() => {});
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [playNotifSound, refreshUnreadCounts]);

  // Fetch unpaid editor payment count for admin/staff/billing sidebar badge
  useEffect(() => {
    if (!['admin', 'staff', 'billing'].includes(user?.role)) return;
    const fetchUnpaid = () => {
      api.get('/editor-payments/summary').then(r => setUnpaidPaymentCount(r.data.unpaid_count || 0)).catch(() => {});
    };
    fetchUnpaid();
    const interval = setInterval(fetchUnpaid, 60000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // Fetch eligible-redemption count for editor sidebar badge (server-side, uses
  // the shared eligibility predicate — same source of truth as the listing).
  useEffect(() => {
    if (user?.role !== 'editor') return;
    const fetchCount = () => {
      api.get('/redemptions/count').then(r => setRedemptionCount(r.data?.count || 0)).catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [user?.role, location.pathname]);

  // Heartbeat for online status + new chat message detection (admin/staff only)
  const lastChatCheck = useRef(new Date().toISOString());
  const [chatPopup, setChatPopup] = useState(null);

  // ── Activity signal riding on the existing heartbeat ──────────────────
  // Tracks whether real input happened since the last beat, so the server can
  // tell "working" from "tab left open". Deliberately rides the beat that
  // already fires every 60s rather than adding a request — no new network
  // activity, no UI, nothing observable changes for the user.
  //
  // We report only our own input state; we never send timestamps. The server
  // stamps and buckets everything from its own clock (see app/activity.py), so
  // a wrong client clock cannot shift when work appears to have happened.
  const act = useRef({ lastInputAt: Date.now(), hadInput: false, inputSeconds: 0, lastSec: 0 });
  useEffect(() => {
    // O(1), no allocation — this runs on every mousemove, so it must stay cheap.
    const onInput = () => {
      const t = Date.now();
      const a = act.current;
      a.lastInputAt = t;
      a.hadInput = true;
      const s = Math.floor(t / 1000);
      if (s !== a.lastSec) { a.lastSec = s; a.inputSeconds += 1; }
    };
    // passive:true so we can never delay scrolling or typing.
    const opts = { passive: true, capture: true };
    const evts = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart', 'focus'];
    evts.forEach(e => window.addEventListener(e, onInput, opts));
    return () => evts.forEach(e => window.removeEventListener(e, onInput, opts));
  }, []);

  useEffect(() => {
    const isAdminOrStaff = user?.role === 'admin' || user?.role === 'staff';
    // Snapshot + reset the interval's accumulator.
    const takeActivity = () => {
      const a = act.current;
      const payload = {
        had_input: a.hadInput,
        visible: typeof document !== 'undefined' && document.visibilityState === 'visible',
        idle_seconds: Math.round((Date.now() - a.lastInputAt) / 1000),
        input_seconds: a.inputSeconds,
      };
      a.hadInput = false;
      a.inputSeconds = 0;
      return payload;
    };
    const beat = () => {
      api.post('/heartbeat', takeActivity()).catch(() => {});
      // Only admin/staff get chat pop-up notifications
      if (isAdminOrStaff) {
        api.get(`/chat/new-messages?since=${lastChatCheck.current}`).then(r => {
          if (r.data && r.data.length > 0) {
            // Don't show popup for your own messages
            const otherMsgs = r.data.filter(m => m.author_id !== user?.id);
            if (otherMsgs.length > 0) {
              const msg = otherMsgs[0];
              setChatPopup({ sender: msg.author_name, body: msg.body?.substring(0, 80), project_id: msg.project_id, project_title: msg.project_title });
              setTimeout(() => setChatPopup(null), 8000);
            }
          }
          lastChatCheck.current = new Date().toISOString();
        }).catch(() => {});
      }
    };
    beat();
    const iv = setInterval(beat, 60000);

    // Tail capture: closing the tab or backgrounding mid-interval would
    // otherwise drop the last partial minute. fetch(keepalive) survives
    // teardown. Using fetch rather than navigator.sendBeacon because auth is a
    // cookie on a cross-origin API (lib/api.js sets withCredentials) and
    // sendBeacon can't set credentials mode.
    const flush = () => {
      const a = act.current;
      if (!a.hadInput) return;              // nothing to report; stay quiet
      try {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/heartbeat`, {
          method: 'POST',
          keepalive: true,
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(takeActivity()),
        }).catch(() => {});
      } catch { /* never let telemetry break unload */ }
    };
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onHide);

    return () => {
      clearInterval(iv);
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [user?.role, user?.id]);

  const sideW = collapsed ? 'w-16' : 'w-60';
  const mainML = collapsed ? 'lg:ml-16' : 'lg:ml-60';

  const navContent = (isMobile) => (
    <>
      {/* Brand + collapse/close */}
      <div className="p-4 border-b border-ug-border-subtle flex items-center justify-between">
        {(!collapsed || isMobile) ? (
          <div onClick={handleLogoClick} className="cursor-pointer select-none flex items-center gap-2.5">
            {/* The website's gem mark, before the word as its nav sets it; 2x and 3x files for sharp screens */}
            <img src="/gem-mark-48.png" srcSet="/gem-mark-48.png 2x, /gem-mark-72.png 3x" alt="" className="w-6 h-6" />
            {/* Set solid, as the website sets the word: the h1 is then the height of the word's own line, so the gem
                beside it centres on the capitals (on the page's taller line it sits 2px low) */}
            <h1 className={`leading-none transition-all ${sparkle ? 'animate-glow-pulse' : ''}`}><Wordmark size={isMobile ? 20 : 26} /></h1>
          </div>
        ) : (
          <div className="w-full flex justify-center cursor-pointer" onClick={handleLogoClick}><img src="/gem-mark-56.png" srcSet="/gem-mark-56.png 2x, /gem-mark-84.png 3x" alt="UG" className="w-7 h-7" /></div>
        )}
        {isMobile ? (
          <button onClick={() => setMobileOpen(false)} className="tap-sm w-9 h-9 rounded-md flex items-center justify-center text-slate-400 hover:text-silk-cream">
            <X className="w-5 h-5" />
          </button>
        ) : (
          <button onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-collapse-btn"
            className="tap-sm w-7 h-7 rounded-md flex items-center justify-center text-slate-500 hover:text-gold-leaf hover:bg-white/5 transition-all shrink-0 hidden lg:flex">
            {collapsed ? <PanelLeftOpen className="w-4 h-4" strokeWidth={1.5} /> : <PanelLeftClose className="w-4 h-4" strokeWidth={1.5} />}
          </button>
        )}
      </div>

      {/* Gem Bag tier widget — client only, always visible (top of sidebar) */}
      {user?.role === 'client' && (
        <GemTierWidget data={gemTier} collapsed={collapsed} isMobile={isMobile}
          onOpen={() => { navigate('/gem-bag'); if (isMobile) setMobileOpen(false); }} />
      )}

      {/* Nav links */}
      <nav className="flex-1 py-3 px-1.5 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {items.map((item) => {
          const isActive = location.pathname === item.path;
          const childActive = item.children?.some(c => location.pathname === c.path);
          const Icon = item.icon;
          const hasChildren = item.children?.length > 0;
          const isOpen = navExpanded[item.path] || childActive;
          const chatBadge = (item.path === '/admin/chats' || item.path === '/editor/chats') && (unreadCounts.client_chats + unreadCounts.internal_chats) > 0;
          const paymentBadge = item.path === '/admin/payments' && unpaidPaymentCount > 0;
          const redemptionBadge = item.path === '/editor/redemptions' && redemptionCount > 0;
          return (
            <div key={item.path}>
              <div className="flex items-center">
                <button onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                  data-testid={`nav-${item.path.replace(/\//g, '-').slice(1) || 'home'}`}
                  title={collapsed && !isMobile ? item.label : undefined}
                  className={`flex-1 flex items-center gap-2.5 rounded-full transition-all duration-200 border-l-2 border-transparent
                    ${(collapsed && !isMobile) ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5'}
                    ${(isActive || childActive)
                      ? 'bg-[color:var(--ug-accent-wash)] text-ug-gold'
                      : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.04]'
                    }
                    ${redemptionBadge ? 'status-badge-pulse' : ''}`}>
                  <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                  {(!collapsed || isMobile) && <span className="font-sans text-xs font-medium truncate">{item.label}</span>}
                  {chatBadge && (
                    <span className="ml-auto bg-ug-status-red text-white text-[9px] font-mono font-bold px-1.5 py-0.5 rounded shrink-0 min-w-[20px] text-center shadow-[0_0_8px_rgba(196,114,114,0.3)]">
                      {unreadCounts.client_chats + unreadCounts.internal_chats}
                    </span>
                  )}
                  {paymentBadge && (
                    <span className="ml-auto bg-ug-gold/20 text-ug-gold text-[9px] font-mono font-bold px-1.5 py-0.5 rounded shrink-0 min-w-[20px] text-center" data-testid="nav-payments-badge">
                      {unpaidPaymentCount}
                    </span>
                  )}
                  {redemptionBadge && (
                    <span className="ml-auto bg-ug-gold/20 text-ug-gold text-[9px] font-mono font-bold px-1.5 py-0.5 rounded shrink-0 min-w-[20px] text-center shadow-[0_0_8px_rgba(198,169,111,0.35)]" data-testid="nav-redemptions-badge">
                      {redemptionCount}
                    </span>
                  )}
                </button>
                {hasChildren && (!collapsed || isMobile) && (
                  <button onClick={() => setNavExpanded(p => ({ ...p, [item.path]: !p[item.path] }))}
                    className="w-7 h-7 flex items-center justify-center text-slate-500 hover:text-gold-leaf rounded tap-sm shrink-0 transition-colors">
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                )}
              </div>
              {/* Sub-items */}
              {hasChildren && isOpen && (!collapsed || isMobile) && (
                <div className="ml-6 mt-0.5 space-y-0.5">
                  {item.children.map(child => {
                    const cActive = location.pathname === child.path;
                    const CIcon = child.icon;
                    return (
                      <button key={child.path} onClick={() => { navigate(child.path); if (isMobile) setMobileOpen(false); }}
                        data-testid={`nav-${child.path.replace(/\//g, '-').slice(1)}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-all text-xs font-sans font-medium
                          ${cActive ? 'text-ug-gold bg-ug-surface' : 'text-zinc-500 hover:text-zinc-300 hover:bg-ug-raised'}`}>
                        <CIcon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                        {child.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Pixally cross-promo — CLIENTS only, desktop, expanded sidebar. Sits between the nav and
          the footer (the slot the old promo used): the client nav already overflows and scrolls,
          so inside it the card would be clipped rather than pinned to empty space. */}
      {/* Pixally cross-promo: clients and admins (operator, 2026-09-11). Never editors. */}
      {/* The logo variant follows the SAME set as the look (lib/useSiteLook.js). It used to be a separate
          `role === 'client'` check, which would have left an admin with the site look's square card and the staff
          TEXT wordmark: one card, two appearances, for no reason a viewer could see. */}
      {(user?.role === 'client' || user?.role === 'admin') && !isMobile && !collapsed && (
        <div className="mx-3 my-2 shrink-0"><PixallyAd logo={SITE_LOOK_ROLES.has(user?.role)} /></div>
      )}

      {/* Take Tour */}
      <div className="px-3 mb-1">
        <button onClick={() => startTour(user?.role || 'client')} data-testid="take-tour-btn"
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-full text-zinc-500 hover:text-ug-gold hover:bg-white/[0.04] transition-colors ${collapsed && !isMobile ? 'justify-center' : ''}`}>
          <HelpCircle className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          {(!collapsed || isMobile) && <span className="font-sans text-xs font-medium">Take Tour</span>}
        </button>
      </div>

      {/* Achievement Badges */}
      {(!collapsed || isMobile) && sidebarMilestones.length > 0 && (
        <div className="border-t border-ug-border-subtle">
          <SidebarBadges milestones={sidebarMilestones} />
        </div>
      )}
      {/* User — click the avatar/name for Profile, Report Bug and Sign Out */}
      <div className="p-3 border-t border-ug-border-subtle relative" ref={sidebarUserMenuRef}>
        {showSidebarMenu && (
          <div data-testid="sidebar-user-menu"
            className={`ug-pop absolute z-50 bg-ug-raised border border-[color:var(--ug-accent-border)] rounded-2xl shadow-ug-lg overflow-hidden ${(collapsed && !isMobile) ? 'bottom-3 left-full ml-2 w-44' : 'bottom-full left-3 right-3 mb-2'}`}>
            <button onClick={() => { navigate('/profile'); setShowSidebarMenu(false); if (isMobile) setMobileOpen(false); }}
              data-testid="sidebar-menu-profile"
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-silk-cream hover:bg-white/5 transition-colors">
              <UserCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Profile
            </button>
            <button onClick={() => { setBugModalOpen(true); setShowSidebarMenu(false); }} data-testid="report-bug-btn"
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-ug-status-red hover:bg-ug-status-red/5 transition-colors">
              <Bug className="w-3.5 h-3.5" strokeWidth={1.5} /> Report Bug
            </button>
            <button onClick={logout} data-testid="logout-btn"
              className="w-full flex items-center gap-2.5 px-3 py-2.5 border-t border-ug-border-subtle text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-velvet-burgundy hover:bg-velvet-burgundy/10 transition-colors">
              <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign Out
            </button>
          </div>
        )}
        {(!collapsed || isMobile) ? (
          <>
            <button onClick={() => setShowSidebarMenu(v => !v)}
              data-testid="sidebar-user-btn" aria-haspopup="menu" aria-expanded={showSidebarMenu}
              className="w-full flex items-center gap-2.5 p-1.5 -ml-1.5 rounded-xl hover:bg-white/5 transition-colors">
              <div className="ug-avatar w-7 h-7 rounded-full bg-gold-leaf/20 flex items-center justify-center shrink-0 overflow-hidden">
                {user?.profile_photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_photo_url}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} /> : null}
                {(!user?.profile_photo_url) && <span className="font-mono text-[10px] text-gold-leaf">{user?.name?.charAt(0)?.toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[12px] text-silk-cream truncate">{user?.name}</p>
                <p className="font-mono text-[9px] uppercase tracking-wider text-gold-leaf/60">{user?.role === 'admin' ? 'Super Admin' : user?.role === 'staff' ? 'Project Manager' : user?.role === 'billing' ? 'Billing Manager' : user?.role === 'editor' ? 'Editor' : 'Client'}</p>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-slate-500 transition-transform ${showSidebarMenu ? 'rotate-180' : ''}`} strokeWidth={1.5} />
            </button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button onClick={() => setShowSidebarMenu(v => !v)} data-testid="sidebar-user-btn"
              title={`${user?.name} · account`} aria-haspopup="menu" aria-expanded={showSidebarMenu}
              className="ug-avatar w-7 h-7 rounded-full bg-gold-leaf/20 flex items-center justify-center hover:bg-gold-leaf/30 transition-colors overflow-hidden">
              {user?.profile_photo_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_photo_url}`} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} /> : null}
              {(!user?.profile_photo_url) && <span className="font-mono text-[10px] text-gold-leaf">{user?.name?.charAt(0)?.toUpperCase()}</span>}
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className={`flex h-[100dvh] overflow-hidden bg-ug-base ${seasonalTheme ? 'seasonal-ambient' : ''}`} style={seasonalStyles}>
      {/* ─── Desktop sidebar (hidden on mobile) ─── */}
      <aside className={`${sideW} bg-ug-deepest border-r border-ug-border-subtle flex-col fixed top-8 h-[calc(100%-32px)] z-30 transition-all duration-300 hidden lg:flex`} data-testid="sidebar">
        {navContent(false)}
      </aside>

      {/* ─── Mobile overlay sidebar ─── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" style={{ top: '32px' }} onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <aside data-testid="sidebar-mobile" className="absolute left-0 top-0 bottom-0 w-72 bg-ug-deepest border-r border-ug-border-subtle flex flex-col animate-slide-in-left"
            onClick={e => e.stopPropagation()}>
            {navContent(true)}
          </aside>
        </div>
      )}

      {/* Bug Report Modal: rendered once, at the root. It used to sit inside the sidebar's content, which on
          a phone is the display:none desktop sidebar (or the mobile one, only while open), so a page that opened
          it (the Help Center's Report a Bug, lib/bugReport.js) showed nothing there. */}
      {bugModalOpen && (
        <div className="ug-modal fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={() => { if (!bugSubmitted) { setBugModalOpen(false); } }} data-testid="bug-report-modal">
          <div className="bg-ug-raised border border-ug-border rounded-xl w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
            {bugSubmitted ? (
              <div className="py-8 text-center">
                <div className="w-12 h-12 rounded-full bg-oxidized-teal/20 border border-oxidized-teal/30 flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 text-oxidized-teal" />
                </div>
                <h3 data-role="section-title" className="font-display text-lg text-zinc-100 mb-1">Report Received</h3>
                <p className="text-[11px] text-zinc-500">Thank you. We'll look into this right away.</p>
              </div>
            ) : (
            <>
            <h3 data-role="section-title" className="font-display text-lg text-zinc-100">Report a Bug</h3>
            <p className="text-[11px] text-zinc-500">Describe what happened and we'll look into it. Your current page and account info are captured automatically.</p>
            <textarea value={bugText} onChange={e => setBugText(e.target.value)} rows={4} placeholder="What went wrong? What were you trying to do?"
              className="w-full bg-ug-surface border border-ug-border text-zinc-200 rounded-xl p-3 text-sm resize-none placeholder:text-zinc-600" data-testid="bug-report-input" />
            <div>
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className={`px-3 py-1.5 rounded-lg border text-[10px] font-sans font-medium transition-colors ${bugScreenshot ? 'border-oxidized-teal/30 bg-oxidized-teal/10 text-oxidized-teal' : 'border-ug-border bg-ug-surface text-zinc-500 group-hover:text-zinc-300 group-hover:border-zinc-500'}`}>
                  {bugScreenshot ? `${bugScreenshot.name.slice(0, 25)}${bugScreenshot.name.length > 25 ? '...' : ''}` : 'Attach Screenshot (optional)'}
                </div>
                <input type="file" accept="image/*" className="hidden" data-testid="bug-screenshot-input"
                  onChange={e => setBugScreenshot(e.target.files?.[0] || null)} />
                {bugScreenshot && <button onClick={(e) => { e.preventDefault(); setBugScreenshot(null); }} className="text-[9px] text-zinc-600 hover:text-ug-status-red">Remove</button>}
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setBugModalOpen(false); setBugScreenshot(null); }} className="px-4 py-2 text-[10px] font-mono uppercase text-slate-500 hover:text-silk-cream transition-colors">Cancel</button>
              <button onClick={async () => {
                if (!bugText.trim()) return;
                try {
                  const formData = new FormData();
                  formData.append('description', bugText);
                  formData.append('page_url', window.location.href);
                  formData.append('browser', navigator.userAgent);
                  formData.append('screen_size', `${window.screen.width}x${window.screen.height}`);
                  formData.append('viewport', `${window.innerWidth}x${window.innerHeight}`);
                  formData.append('platform', navigator.platform || '');
                  if (bugScreenshot) formData.append('screenshot', bugScreenshot);
                  await api.post('/bug-reports', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                  setBugSubmitted(true);
                  setBugText(''); setBugScreenshot(null);
                  setTimeout(() => { setBugModalOpen(false); setBugSubmitted(false); }, 2000);
                } catch { toast.error('Failed to submit'); }
              }} data-testid="submit-bug-btn"
                className="px-5 py-2 rounded-md bg-velvet-burgundy text-white font-mono text-[10px] uppercase tracking-wider hover:bg-velvet-burgundy/90 transition-colors">
                Submit
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* ─── Mobile top bar (shown on mobile only) ─── */}
      <div className="fixed top-8 left-0 right-0 h-14 bg-ug-deepest border-b border-ug-border-subtle z-20 flex lg:hidden items-center px-4 gap-3" data-testid="mobile-top-bar">
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn"
          className="w-10 h-10 rounded-md flex items-center justify-center text-slate-400 hover:text-gold-leaf hover:bg-white/5 transition-all">
          <Menu className="w-5 h-5" strokeWidth={1.5} />
        </button>
        {/* Set solid like the sidebar's word, so the gem centres on the capitals */}
        <h1 className="flex-1 flex items-center gap-2.5 leading-none">
          <img src="/gem-mark-40.png" srcSet="/gem-mark-40.png 2x, /gem-mark-60.png 3x" alt="" className="w-5 h-5" />
          <Wordmark size={20} />
        </h1>
        {/* Mobile: notification + user */}
        <div className="flex items-center gap-2">
          <div className="relative" ref={userMenuRefMobile}>
            <button onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifs(false); }}
              data-testid="user-avatar-mobile"
              className="ug-avatar w-8 h-8 rounded-full bg-gold-leaf/20 flex items-center justify-center hover:bg-gold-leaf/30 transition-colors">
              <span className="font-mono text-[10px] text-gold-leaf">{user?.name?.charAt(0)?.toUpperCase()}</span>
            </button>
            {showUserMenu && (
              <div className="absolute right-0 top-11 w-52 bg-ug-raised border border-ug-border rounded-xl shadow-ug-lg overflow-hidden z-50" data-testid="user-dropdown">
                <div className="px-3 py-2.5 border-b border-ug-border-subtle">
                  <p className="text-[12px] text-silk-cream">{user?.name}</p>
                  <p className="font-mono text-[9px] uppercase tracking-wider text-gold-leaf/60">{user?.role === 'admin' ? 'Super Admin' : user?.role === 'staff' ? 'Project Manager' : user?.role === 'billing' ? 'Billing Manager' : user?.role === 'editor' ? 'Editor' : 'Client'}</p>
                </div>
                <button onClick={() => { navigate('/profile'); setShowUserMenu(false); }}
                  data-testid="user-menu-profile"
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-silk-cream hover:bg-white/5 transition-colors">
                  <UserCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Profile
                </button>
                <button onClick={() => { logout(); setShowUserMenu(false); }}
                  data-testid="logout-btn"
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-velvet-burgundy hover:bg-velvet-burgundy/10 transition-colors">
                  <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Notification bell, portalled into the brand bar's slot ───
          It sits beside the appearance control at top-right. BrandBar is 32px
          with overflow:hidden, so the dropdown is position:fixed to escape the
          clip — the same reason the theme menu is fixed. */}
      {brandSlot && createPortal(
        <div ref={notifRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '7px' }}>
          {/* Refer and earn: clients only (lib/referral.js), to the Gem Bag's referral card. The same
              pill as the bell and the appearance control, with the label set in the appearance
              control's type. On a phone the label goes (.brandbar-refer-label, index.css): with it
              the bar's own name was clipped to "UNCUT"; the title carries the words. */}
          {showReferButton(user) && (
            <button onClick={() => navigate(REFER_TARGET)} data-testid="refer-and-earn"
              className="tap-auto brandbar-pill" title={REFER_LABEL} aria-label={REFER_LABEL}
              style={{ padding: '0 8px 0 7px', gap: '5px' }}>
              <Gift style={{ width: '11px', height: '11px', color: '#2bb3a8' }} strokeWidth={2} />
              <span className="brandbar-refer-label" style={{ fontSize: '8px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.62)', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>{REFER_LABEL}</span>
            </button>
          )}
          <button
            onClick={() => { setShowNotifs(v => !v); if (!showNotifs) api.get('/notifications').then(r => setNotifs(r.data)).catch(() => {}); }}
            data-testid="notif-bell"
            className="tap-auto brandbar-pill"
            aria-haspopup="menu" aria-expanded={showNotifs}
            title={notifCount > 0 ? `${notifCount} unread` : 'Notifications'}
            style={{ padding: '0 8px', gap: '5px', position: 'relative' }}>
            <Bell className={notifCount > 0 ? 'brandbar-bell-icon' : undefined}
              style={{ width: '11px', height: '11px', color: notifCount > 0 ? '#2bb3a8' : 'rgba(255,255,255,0.55)' }} strokeWidth={2} />
            {notifCount > 0 && (
              <span style={{
                fontSize: '8px', fontWeight: 700, lineHeight: 1, color: '#0a0907',
                background: '#2bb3a8', borderRadius: '999px', padding: '2px 5px',
                fontFamily: "'DM Sans', sans-serif",
              }}>{notifCount > 99 ? '99+' : notifCount}</span>
            )}
          </button>

          {showNotifs && (
            <div data-testid="notif-dropdown" className="brandbar-menu" style={{
              position: 'fixed', top: '38px', right: '10px', width: '300px',
              background: '#14161a', border: '1px solid rgba(43,179,168,0.22)',
              borderRadius: '14px', boxShadow: '0 10px 30px -6px rgba(0,0,0,0.55)',
              overflow: 'hidden', zIndex: 70,
            }}>
              {/* Colours come from the brandbar-notif-* classes (index.css), not theme classes: the menu
                  is dark in both themes, like the bar it hangs from. */}
              <div className="brandbar-notif-head px-3 py-2 flex items-center justify-between">
                <span className="brandbar-notif-title font-mono text-[10px] uppercase tracking-wider">Notifications</span>
                {notifs.length > 0 && (
                  <button onClick={() => { api.put('/notifications/read', {}).then(() => { setNotifCount(0); setNotifs([]); setShowNotifs(false); }).catch(() => toast.error('Failed to mark as read')); }}
                    data-testid="notif-mark-all-read"
                    className="tap-auto brandbar-notif-action text-[9px] font-mono transition-colors">Mark all read</button>
                )}
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifs.length === 0 && <p className="brandbar-notif-empty text-[11px] text-center py-6">You're all caught up</p>}
                {notifs.map(n => (
                  <button key={n.id} onClick={() => handleNotifClick(n)} data-testid={`notif-item-${n.id}`}
                    data-urgent={n.type === 'urgent' ? 'true' : undefined}
                    className="brandbar-notif-row w-full text-left px-3 py-2.5 cursor-pointer transition-colors">
                    <p className="brandbar-notif-msg text-[11px] leading-relaxed">{n.message}</p>
                    <p className="brandbar-notif-time font-mono text-[8px] mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>, brandSlot)}

      {/* ─── Main content ─── */}
      <main className={`${mainML} flex-1 min-w-0 h-[100dvh] overflow-hidden aurora-bg transition-all duration-300 pt-[88px] lg:pt-0`} data-testid="main-content">
        <div className="h-[calc(100dvh-88px)] lg:h-[100dvh] overflow-hidden flex flex-col">
          {user?.email_verified === false && (
            <div className="shrink-0 bg-ug-gold/10 border-b border-ug-gold/20 px-4 py-2 flex items-center justify-between" data-testid="email-verify-banner">
              <span className="font-sans text-[11px] text-ug-gold">Please verify your email address to place orders and receive project updates.</span>
              <button onClick={() => { api.post('/auth/resend-verification').then(() => toast.success('Verification email sent!')).catch(() => toast.error('Failed to resend')); }}
                className="font-sans text-[10px] font-semibold text-ug-gold underline underline-offset-2 shrink-0 ml-4">Resend Email</button>
            </div>
          )}
          {(user?.role === 'client' || user?.role === 'editor') && showRefreshTip && (
            <div className="shrink-0 bg-oxidized-teal/10 border-b border-oxidized-teal/20 px-4 py-2 flex items-start sm:items-center justify-between gap-3" data-testid="hard-refresh-banner">
              <div className="flex items-start sm:items-center gap-2 min-w-0">
                <RefreshCw className="w-3.5 h-3.5 text-oxidized-teal shrink-0 mt-0.5 sm:mt-0" strokeWidth={2} />
                <span className="font-sans text-[11px] text-oxidized-teal leading-snug">
                  Video playback or downloads acting up? Try a hard refresh first. We ship portal updates often, and your browser sometimes needs a nudge to load the latest version and clear up glitches.
                  <span className="hidden sm:inline"> &nbsp;Windows: <strong className="font-semibold">Ctrl + Shift + R</strong> · Mac: <strong className="font-semibold">Cmd + Shift + R</strong></span>
                  <span className="block sm:hidden mt-0.5 text-[10px] opacity-90">Windows: Ctrl + Shift + R &nbsp;·&nbsp; Mac: Cmd + Shift + R</span>
                </span>
              </div>
              <button onClick={() => setShowRefreshTip(false)} data-testid="dismiss-refresh-banner-btn" aria-label="Dismiss"
                className="shrink-0 text-oxidized-teal/70 hover:text-oxidized-teal transition-colors p-0.5">
                <X className="w-4 h-4" strokeWidth={2} />
              </button>
            </div>
          )}
          {BANNER_ROLES.includes(user?.role) && <FootagePullNewVersionBanner role={user?.role} />}
          <div className="flex-1 overflow-hidden">
            <UnreadRefreshContext.Provider value={refreshUnreadCounts}>
              {children}
            </UnreadRefreshContext.Provider>
          </div>
        </div>
      </main>

      {/* Product Tour */}
      <ProductTour
        steps={user?.role === 'client' ? CLIENT_STEPS : user?.role === 'editor' ? EDITOR_STEPS : user?.role === 'staff' ? STAFF_STEPS : ADMIN_STEPS}
        tourKey={user?.role || 'client'}
      />

      {/* Gem-bag tier celebration — one-shot, server-flag driven (client only).
          Both actions clear the flag server-side so it never re-fires cross-device. */}
      {user?.role === 'client' && gemTier?.tier_celebration_pending && (
        <TierCelebration
          celebration={gemTier.tier_celebration_pending}
          nextTier={gemTier.next_tier}
          spendToNext={gemTier.spend_to_next}
          onDismiss={clearTierCelebration}
          onViewGemBag={() => { clearTierCelebration(); navigate('/gem-bag'); }}
        />
      )}

      {/* Chat pop-up notification — admin/staff only. At the top right, under the top bar: at the bottom right it sat
          over the chat box, and a click meant to land in the box opened the pop-up's chat instead, which reloaded the
          page and threw away what was being typed (a staff request, 2026-11-03: "I still want the alert but can it be
          at the top right?"). Opening its chat no longer reloads the page (lib/openProject.js), and each chat keeps
          its unsent words (lib/chatBox.js). */}
      {chatPopup && (
        <div className="fixed top-24 right-4 lg:top-12 lg:right-6 z-[9990] animate-slide-in-right" onClick={() => {
          requestOpenProject(chatPopup.project_id, 'notes');
          navigate('/dashboard');
          setChatPopup(null);
        }}>
          {/* It has to catch the eye from anywhere in the portal, so it does NOT sit on the card
              surface: bg-ug-raised (#1a1d21) measured 1.08:1 against the page (#131518) in dark, which
              is why a new message could slide in unnoticed (operator, 2026-09-17). bg-ug-surface with a
              gold edge and a solid gold left bar is the same vocabulary the announcements use, two steps
              louder. Light mode remaps the surface to white (index.css), where ug-surface is 1.05:1 on
              the page. */}
          <div data-testid="chat-popup" className="bg-ug-surface border border-ug-gold/45 border-l-[3px] border-l-gold-leaf rounded-xl shadow-ug-lg p-4 max-w-sm cursor-pointer hover:border-ug-gold/70 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gold-leaf/20 flex items-center justify-center shrink-0">
                <MessageSquare className="w-4 h-4 text-gold-leaf" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] text-gold-leaf font-mono">{chatPopup.sender}</p>
                <p className="text-[10px] text-slate-500 truncate">{chatPopup.project_title}</p>
                <p className="text-[12px] text-silk-cream mt-1 line-clamp-2">{chatPopup.body}</p>
              </div>
              <button onClick={(e) => { e.stopPropagation(); setChatPopup(null); }} className="text-slate-600 hover:text-slate-400 shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
